/**
 * Formatierung fuer die Buchhaltung.
 *
 * Uebernommen aus `finance-web/src/lib/format.ts`, gekuerzt auf das, was hier
 * vorkommt, und um zwei Dinge ergaenzt, die es dort nicht gibt: `faelligText`
 * (die Zu-zahlen-Liste sagt „in 3 Tagen", nicht „2026-09-10") und `dateiGroesse`.
 *
 * Betraege kommen als String vom Server (`Decimal`). Fuer die Anzeige wird
 * einmal in `Number` gewandelt — unkritisch, weil hier nichts mehr gerechnet,
 * sondern nur dargestellt wird.
 */

const EUR = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Vollstaendiger Betrag mit Cent, z. B. "1.234,56 €". */
export function euro(betrag: string | number | null | undefined): string {
  if (betrag === null || betrag === undefined || betrag === '') return '—';
  const zahl = Number(betrag);
  if (Number.isNaN(zahl)) return String(betrag);
  return EUR.format(zahl);
}

/** "07.09.2026" */
export function datum(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return wert;
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** "07.09.2026, 14:30" — fuer Log-Zeilen und „letzter Lauf". */
export function datumZeit(wert: string | null | undefined): string {
  if (!wert) return '—';
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return wert;
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Faelligkeit in Worten.
 *
 * Der Server liefert `tage_bis_faellig` vorzeichenbehaftet, negativ heisst
 * ueberfaellig. „in 3 Tagen" und „seit 3 Tagen ueberfaellig" sind zwei
 * verschiedene Nachrichten, und nur eine davon darf man ueberlesen.
 */
export function faelligText(tage: number | null | undefined): string {
  if (tage === null || tage === undefined) return '—';
  if (tage < -1) return `${Math.abs(tage)} Tage überfällig`;
  if (tage === -1) return '1 Tag überfällig';
  if (tage === 0) return 'heute fällig';
  if (tage === 1) return 'morgen fällig';
  return `in ${tage} Tagen`;
}

/** "vor 3 h" — fuer den Status-Punkt in der Kopfzeile. */
export function alterText(stunden: number | null | undefined): string {
  if (stunden === null || stunden === undefined) return 'noch kein Lauf';
  if (stunden < 1) return `vor ${Math.max(1, Math.round(stunden * 60))} min`;
  if (stunden < 48) return `vor ${Math.round(stunden)} h`;
  return `vor ${Math.round(stunden / 24)} Tagen`;
}

/** "1,2 MB" */
export function dateiGroesse(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toLocaleString('de-DE', { maximumFractionDigits: 0 })} kB`;
  }
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', {
    maximumFractionDigits: 1,
  })} MB`;
}

/** IBAN in Vierergruppen — so steht sie auf dem Ueberweisungstraeger. */
export function iban(wert: string | null | undefined): string {
  if (!wert) return '—';
  return wert.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
}

/** Heute als JJJJ-MM-TT, fuer `<input type="date">`. */
export function heuteIso(): string {
  const jetzt = new Date();
  const versatz = jetzt.getTimezoneOffset() * 60_000;
  return new Date(jetzt.getTime() - versatz).toISOString().slice(0, 10);
}
